import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  ChangeDetectorRef,
  Component,
  Injector,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FieldType, FormlyFieldConfig } from '@ngx-formly/core';
import { FormBuilderService } from '../../../core/services/form-builder.service';
import { Subject, takeUntil } from 'rxjs';
import { FormBuilderManipulatorClass } from '../../../core/utils/form-builder-manipulator-class.utils';
import { EDragDropItemsIds } from '../../../core/models/form-builder-drag-item-ids.enum';
import { alphaNanoIdUtil } from '@irembo-andela/irembogov3-common';
import { generateNewFieldKeyFromSettingsLabel } from '../../../core/utils/form-fields-formly-configurations/form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';

@Component({
  selector: 'irembogov-formly-field-group',
  templateUrl: './formly-field-group.component.html',
  styleUrls: ['./formly-field-group.component.scss'],
})
export class FormlyFieldGroupComponent
  extends FieldType
  implements OnInit, OnDestroy
{
  reviewModeEnabledSub$: Subject<void> = new Subject<void>();
  reviewModeEnabled = false;
  isSelected = false;
  fieldBlockDragListId = `${
    EDragDropItemsIds.DefaultFieldListListItems
  }${alphaNanoIdUtil(5)}`;
  connectedBlockIds: string[] = [];
  formBuilderManipulator!: FormBuilderManipulatorClass;

  constructor(
    private formBuilderService: FormBuilderService,
    private injector: Injector,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    super();
    this.formBuilderManipulator = new FormBuilderManipulatorClass(
      this.injector
    );
  }

  ngOnInit(): void {
    this.formBuilderService.addToFormFieldBlockIdsSet(
      this.fieldBlockDragListId
    );

    this.formBuilderService.reviewModeEnabled$
      .pipe(takeUntil(this.reviewModeEnabledSub$))
      .subscribe((isEnabled: boolean) => {
        this.reviewModeEnabled = isEnabled;
      });

    this.formBuilderService.formFieldBlockIdsSet$
      .pipe(takeUntil(this.reviewModeEnabledSub$))
      .subscribe((blockIdSet: Set<string>) => {
        const fieldIds = new Set(Array.from(blockIdSet));
        fieldIds.delete(this.fieldBlockDragListId);
        fieldIds.add(EDragDropItemsIds.MainBuilderFieldTypesListItems);
        fieldIds.add(EDragDropItemsIds.MainBuilderWidgetsListItems);
        this.connectedBlockIds = Array.from(fieldIds);
        this.changeDetectorRef.detectChanges();
      });
  }

  isValid(field: FormlyFieldConfig): boolean {
    if (field.key && field.formControl) {
      return field.formControl.valid;
    }

    return field.fieldGroup
      ? field.fieldGroup.every(f => this.isValid(f))
      : true;
  }

  dropBlock(event: CdkDragDrop<any[]>) {
    if (
      event.previousContainer.id ===
        <string>EDragDropItemsIds.MainBuilderFieldTypesListItems ||
      event.previousContainer.id ===
        <string>EDragDropItemsIds.MainBuilderWidgetsListItems
    ) {
      const newFieldItem: FormlyFieldConfig = event.item.data;
      const repeaterWrapper = document
        .elementFromPoint(event.dropPoint.x, event.dropPoint.y)
        ?.closest('.irembogov-custom-repeater');

      if (repeaterWrapper) {
        const repeaterId = repeaterWrapper.getAttribute('data-field-id');
        const repeaterField = this.findFieldById(
          repeaterId,
          this.field.fieldGroup
        );

        if (repeaterField && repeaterField.type === 'customrepeater') {
          this.addNewFieldItemToRepeater(repeaterField, newFieldItem);
          return;
        }
      }
      this.addNewFieldItemToFormlyForm(newFieldItem);
      return;
    }
    if (
      event.previousContainer.id ===
        <string>EDragDropItemsIds.MainBuilderFieldTypesListItems ||
      event.previousContainer.id ===
        <string>EDragDropItemsIds.MainBuilderWidgetsListItems
    ) {
      const newFieldItem: FormlyFieldConfig = event.item.data;
      this.addNewFieldItemToFormlyForm(newFieldItem);
      return;
    }

    this.formBuilderManipulator.moveBlock(event);
    return;
  }

  private addNewFieldItemToFormlyForm(
    newFieldItem: FormlyFieldConfig,
    setAsConfiguredItem = true
  ): void {
    newFieldItem = generateNewFieldKeyFromSettingsLabel(
      newFieldItem,
      this.formBuilderService
    );
    if (this.field.fieldGroup) {
      this.field.fieldGroup.push(newFieldItem);
    } else {
      this.field.fieldGroup = [newFieldItem];
    }
    this.formBuilderService.refreshFormlyForm();

    if (setAsConfiguredItem) {
      this.formBuilderService.configureFieldAction$.next({
        ...newFieldItem,
        parent: this.field,
      });
    }
  }

  private addNewFieldItemToRepeater(
    repeaterField: FormlyFieldConfig,
    newFieldItem: FormlyFieldConfig
  ) {
    const clonedField = JSON.parse(JSON.stringify(newFieldItem));
    if (
      !repeaterField.fieldArray ||
      typeof repeaterField.fieldArray === 'function'
    ) {
      repeaterField.fieldArray = { fieldGroup: [] };
    }
    if (!repeaterField.fieldArray.fieldGroup) {
      repeaterField.fieldArray.fieldGroup = [];
    }

    if (!repeaterField.fieldGroup) {
      repeaterField.fieldGroup = [];
    }
    repeaterField.fieldArray.fieldGroup.push(newFieldItem);
    this.formBuilderService.refreshFormlyForm();
    this.formBuilderService.configureFieldAction$.next(clonedField);
  }

  private findFieldById(
    id: string | null,
    fields: FormlyFieldConfig[] = []
  ): FormlyFieldConfig | null {
    if (!id) return null;
    for (const field of fields) {
      if (field.id === id) return field;
      if (field.fieldGroup) {
        const found = this.findFieldById(id, field.fieldGroup);
        if (found) return found;
      }
    }
    return null;
  }

  ngOnDestroy(): void {
    this.reviewModeEnabledSub$.next();
    this.reviewModeEnabledSub$.complete();
  }
}
